import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, CircleNotch, Eye, EyeSlash, Notebook, FirstAid, Sparkle, ShieldCheck, LockKey, EnvelopeSimpleOpen, Smiley } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import KidLogin from "../components/kids/KidLogin";
import { useAuth } from "../context/AuthContext";
import { formatApiError, authApi } from "../lib/api";

export const LOGIN_ART = "https://customer-assets-v7afamib.emergentagent.net/job_plant-records/artifacts/5a940702cd95aa26_IMG_0729.JPG";

export const AUTH_CARD = "relative w-full max-w-[400px] rounded-[28px] bg-background/80 backdrop-blur-2xl backdrop-saturate-150 ring-1 ring-white/40 dark:ring-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)] p-7 sm:p-8";
const FIELD = "h-12 rounded-2xl bg-muted/60 border-0 px-4 text-[15px] focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0";
const FEATURES = [[Notebook, "Care records"], [FirstAid, "Acute care"], [Sparkle, "Plant labels"]];
const MODES = [["login", "Sign in"], ["register", "Register"]];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-medium text-foreground/80 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export function PrivateNotice() {
  return (
    <div className="mt-6 flex items-start gap-2.5 rounded-2xl bg-muted/60 px-4 py-3" data-testid="auth-private-notice">
      <LockKey weight="duotone" className="h-4 w-4 text-foreground/70 mt-0.5 shrink-0" />
      <p className="text-xs text-foreground/75 leading-relaxed"><span className="font-semibold text-foreground">Private system.</span> Plant inventory and care records for The Green House on E Ave. Access is by invitation only and not open to the public.</p>
    </div>
  );
}

export function Backdrop() {
  return (
    <div className="fixed inset-0 -z-0" aria-hidden>
      <img src={LOGIN_ART} alt="" className="absolute inset-0 h-full w-full object-cover object-[72%_center] sm:object-center scale-105" data-testid="login-art" />
      <div className="absolute inset-0 backdrop-blur-md [mask-image:radial-gradient(ellipse_at_center,transparent_35%,black_85%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.05)_30%,rgba(0,0,0,0.5)_100%)]" />
    </div>
  );
}

function ModeToggle({ mode, onChange }) {
  return (
    <div className="relative grid grid-cols-2 p-1 rounded-full bg-muted/70" data-testid="auth-mode-toggle">
      <span aria-hidden className="absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-background shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        style={{ transform: mode === "register" ? "translateX(100%)" : "none" }} />
      {MODES.map(([m, label]) => (
        <button key={m} type="button" data-testid={`auth-mode-${m}`} onClick={() => onChange(m)}
          className={`relative z-10 h-9 rounded-full text-sm transition-colors ${mode === m ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [kidMode, setKidMode] = useState(false);
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [invite, setInvite] = useState(null);
  useEffect(() => {
    if (!inviteToken) return;
    setMode("register");
    authApi.checkInvite(inviteToken).then((i) => { setInvite(i); setForm((f) => ({ ...f, name: i.name || f.name, email: i.email || f.email })); })
      .catch((e) => { setInvite(false); setError(formatApiError(e.response?.data?.detail)); });
  }, [inviteToken]);

  if (user) return <Navigate to={location.state?.from || "/"} replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password, invite ? inviteToken : undefined);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] flex flex-col items-center justify-center px-4 py-10 overflow-hidden" data-testid="auth-page">
      <Backdrop />

      <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
        className={AUTH_CARD}
        data-testid="auth-card">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center"><Leaf weight="duotone" className="h-5 w-5 text-primary" /></span>
          <div className="leading-tight">
            <p className="text-sm font-semibold tracking-tight">The Green House on E Ave</p>
            <p className="text-xs text-muted-foreground">Plant Health Record</p>
          </div>
        </div>

        <h1 className="mt-8 text-[28px] font-semibold tracking-tight leading-tight" data-testid="auth-title">{kidMode ? "Hi there, plant helper!" : invite === false ? "Invite link not valid" : isLogin ? "Welcome back" : invite ? "You're invited" : "Join the care team"}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{kidMode ? "Sign in to see your plants." : invite === false ? "Ask an admin to send you a fresh invite." : isLogin ? "Sign in to pick up where the team left off." : invite ? `Create your ${invite.role === "admin" ? "admin" : "caregiver"} account to get started.` : "Request a caregiver account for the collection."}</p>

        {!kidMode && <PrivateNotice />}

        {!invite && invite !== false && !kidMode && <div className="mt-6"><ModeToggle mode={mode} onChange={(m) => { setMode(m); setError(""); setForgot(false); }} /></div>}

        {kidMode ? <KidLogin onBack={() => setKidMode(false)} /> : invite === false ? (
          <div className="mt-6" data-testid="auth-invite-invalid">
            <p data-testid="auth-error" className="text-sm text-[#FF3B30]">{error}</p>
            <button type="button" onClick={() => { setInvite(null); setError(""); setMode("login"); navigate("/login", { replace: true }); }} data-testid="auth-invite-back"
              className="mt-4 h-11 px-5 rounded-full bg-muted text-sm font-medium">Back to sign in</button>
          </div>
        ) : (
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
          {!isLogin && (
            <Field label="Name"><Input data-testid="auth-name-input" value={form.name} onChange={set("name")} required autoComplete="name" className={FIELD} placeholder="Jane Rivera" /></Field>
          )}
          <Field label="Email"><Input data-testid="auth-email-input" type="email" value={form.email} onChange={set("email")} readOnly={!!invite?.email} required autoComplete="email" className={FIELD} placeholder="you@example.com" /></Field>
          <Field label="Password">
            <div className="relative">
              <Input data-testid="auth-password-input" type={show ? "text" : "password"} value={form.password} onChange={set("password")} required minLength={isLogin ? undefined : 8}
                autoComplete={isLogin ? "current-password" : "new-password"} className={`${FIELD} pr-12`} placeholder={isLogin ? "Your password" : "At least 8 characters"} />
              <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} data-testid="auth-password-toggle"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors">
                {show ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>

          {!isLogin && (
            <div className="flex items-start gap-2.5 rounded-2xl bg-primary/[0.08] px-4 py-3" data-testid="auth-approval-note">
              {invite ? <EnvelopeSimpleOpen weight="duotone" className="h-4 w-4 text-primary mt-0.5 shrink-0" /> : <ShieldCheck weight="duotone" className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
              <p className="text-xs text-foreground/75 leading-relaxed">{invite ? "Your invite is valid. Your account will be ready the moment you finish." : "New accounts are reviewed by an admin. You'll be able to sign in as soon as you're approved."}</p>
            </div>
          )}

          {isLogin && (
            <div className="-mt-1 text-right">
              <button type="button" onClick={() => setForgot((v) => !v)} data-testid="auth-forgot-btn" className="text-xs font-medium text-primary hover:underline underline-offset-4">Forgot password?</button>
            </div>
          )}
          {isLogin && forgot && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-muted/60 px-4 py-3 text-xs text-foreground/80 leading-relaxed" data-testid="auth-forgot-help">
              Ask an admin to send you a one-time reset link from the Team page. Open it, choose a new password and you're back in. Links expire after 24 hours.
            </motion.div>
          )}

          {error && <p data-testid="auth-error" className="text-sm text-[#FF3B30] px-1">{error}</p>}

          <button data-testid="auth-submit-btn" type="submit" disabled={busy}
            className="w-full h-12 rounded-full bg-primary text-primary-foreground text-[15px] font-semibold inline-flex items-center justify-center gap-2 shadow-[0_8px_20px_-8px_rgba(52,199,89,0.6)] hover:brightness-105 active:scale-[0.98] disabled:opacity-60 transition-[transform,filter,opacity]">
            {busy && <CircleNotch className="h-4 w-4 animate-spin" />}
            {isLogin ? "Sign in" : invite ? "Create account" : "Request access"}
          </button>
        </form>
        )}
        {!kidMode && isLogin && !invite && (
          <button type="button" onClick={() => { setKidMode(true); setError(""); }} data-testid="auth-kids-btn"
            className="mt-5 w-full h-12 rounded-full bg-[#FFB800]/15 text-[#B07A00] dark:text-[#FFCC4D] text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#FFB800]/25 active:scale-[0.98] transition-[background-color,transform]">
            <Smiley weight="fill" className="h-5 w-5" />Kids sign in here
          </button>
        )}
      </motion.div>

      <motion.ul initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.5 }}
        className="relative mt-5 flex flex-wrap justify-center gap-2" data-testid="auth-features">
        {FEATURES.map(([I, label]) => (
          <li key={label} className="h-8 px-3.5 rounded-full bg-black/30 backdrop-blur-xl ring-1 ring-white/15 text-white/90 text-xs font-medium inline-flex items-center gap-1.5">
            <I weight="duotone" className="h-3.5 w-3.5" />{label}
          </li>
        ))}
      </motion.ul>
    </div>
  );
}
